
import {
  computed, defineComponent, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import VirtualScroller from 'primevue/virtualscroller';

// Height of a row of the list, and rows shown before it scrolls
const ROW_HEIGHT = 40;
const MAX_ROWS = 8;

/**
 * Window to manage the values of a field holding many of them (hundreds of
 * URLs, codes…): add a value or a pasted list, search, remove, copy. It works
 * on a copy of the values: only « Valider » gives the new list back.
 */
export default defineComponent({
  name: 'SegmentValuesDialog',

  components: {
    Dialog,
    Button,
    InputText,
    Textarea,
    VirtualScroller,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },

    values: {
      type: Array as PropType<unknown[]>,
      required: true,
    },

    // Property holding the label when the values are objects
    field: {
      type: String,
      required: false,
      default: undefined,
    },

    title: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['update:visible', 'validate'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const draft = ref<unknown[]>([]);
    const addText = ref('');
    const addMessage = ref<{ text: string; error: boolean } | null>(null);
    const search = ref('');
    const copied = ref(false);

    const labelOf = (value: unknown): string => {
      if (value !== null && typeof value === 'object' && props.field) {
        return String((value as Record<string, unknown>)[props.field] ?? '');
      }
      return String(value ?? '');
    };

    // Each opening starts from the values of the field
    watch(() => props.visible, (visible) => {
      if (!visible) {
        return;
      }
      draft.value = [...(props.values ?? [])];
      addText.value = '';
      addMessage.value = null;
      search.value = '';
      copied.value = false;
    }, { immediate: true });

    const countLabel = (count: number): string => {
      if (count === 0) {
        return t('segmentation.valuesField.countNone');
      }
      return count === 1
        ? t('segmentation.valuesField.countOne')
        : t('segmentation.valuesField.count', { count });
    };

    const filteredItems = computed(() => {
      const query = search.value.trim().toLowerCase();
      return draft.value
        .map((value) => ({ value, label: labelOf(value) }))
        .filter((item) => !query || item.label.toLowerCase().includes(query));
    });

    const listHeight = computed(() => `${Math.min(filteredItems.value.length, MAX_ROWS) * ROW_HEIGHT}px`);

    // One value per line, trimmed, blank lines dropped, values already listed kept once
    const addFromText = () => {
      const values = addText.value.split(/\r?\n/).map((value) => value.trim()).filter((value) => value !== '');
      if (!values.length) {
        addMessage.value = { text: t('segmentation.valuesField.addEmpty'), error: true };
        return;
      }

      const labels = new Set(draft.value.map(labelOf));
      const next = [...draft.value];
      let added = 0;
      let repeated = 0;
      values.forEach((value) => {
        if (labels.has(value)) {
          repeated += 1;
          return;
        }
        labels.add(value);
        next.push(value);
        added += 1;
      });

      draft.value = next;
      addText.value = '';
      const text = repeated
        ? t('segmentation.valuesField.addedWithRepeats', { added, repeated })
        : t('segmentation.valuesField.added', { added });
      addMessage.value = { text, error: false };
    };

    const remove = (value: unknown) => {
      draft.value = draft.value.filter((item) => item !== value);
    };

    const clearAll = () => {
      draft.value = [];
    };

    const copy = async () => {
      try {
        await navigator.clipboard.writeText(draft.value.map(labelOf).join('\n'));
        copied.value = true;
        setTimeout(() => {
          copied.value = false;
        }, 2000);
      } catch {
        addMessage.value = { text: t('segmentation.valuesField.copyFailed'), error: true };
      }
    };

    const close = () => {
      emit('update:visible', false);
    };

    const validate = () => {
      emit('validate', [...draft.value]);
      emit('update:visible', false);
    };

    return {
      t,
      draft,
      addText,
      addMessage,
      search,
      copied,
      rowHeight: ROW_HEIGHT,
      countLabel,
      filteredItems,
      listHeight,
      addFromText,
      remove,
      clearAll,
      copy,
      close,
      validate,
    };
  },
});
